import React, { useState, useEffect, useCallback, useRef, Dispatch, SetStateAction } from 'react';
import {
  searchReportingFetch as fetch,
  searchReportingLegacyUrl,
} from '../../../lib/flags/search-reporting';
import { Label } from '@/components/ui/label';
import Checkbox from '../../../components/ui/checkbox';
import { getAuthToken } from '../../../utils/auth';
import { Input } from '@/components/ui/input';
import { AddressAutofill as AddressAutofillOriginal } from '@mapbox/search-js-react';
import { getCountryOptions } from '@charitystack/shared';
// @ts-expect-error - JS file without type declarations
import { queryContactsTopContacts } from '../../../utils/RocksetQueries/ContactsQueries';
import ContactMultiSelectDropdown from '../../dropdowns/ContactMultiSelectDropdown';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString, isPossiblePhoneNumber } from 'libphonenumber-js';
import type {
  Contact,
  ContactEmail,
  ContactPhone,
  ContactAddress,
  AddressAutocompleteEvent,
} from '../../drawers/types/FundraisingPaymentTypes';
import type { Merchant } from '../../../types/common';

// Cast AddressAutofill to work around @mapbox/search-js-react type incompatibility with React 18
const AddressAutofill = AddressAutofillOriginal as React.ComponentType<{
  accessToken: string;
  onRetrieve: (event: AddressAutocompleteEvent) => void;
  children: React.ReactNode;
}>;

// Country option type
interface CountryOption {
  value: string;
  label: string;
}

// Get countries list (frozen canonical options, PE-1878)
const countries: readonly CountryOption[] = getCountryOptions();
// PE-1878 FE window: a stored country value ("Israel"/"Palestine, State of")
// may no longer match a canonical option before the data backfill runs. Keep
// any orphan value selectable so an existing row doesn't silently go blank
// (and a re-save doesn't drop the country). Mirrors CreateEditContactDrawer.
const countryLabels = new Set(countries.map((c) => c.label));

// Base form data interface with contact fields
// This is the minimum required fields for ContactInformation to work
interface BaseContactFormData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  organizationName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  region?: string;
  zip?: string;
  country?: string;
  contactID?: string;
  contactSearch?: string;
  anonymous?: boolean;
  communicationConsent?: boolean;
}

// Base transaction interface for edit mode
// This accepts any object that has the contact-related fields we need
interface BaseTransaction {
  transactionID?: string;
  contactID?: string;
  email?: string;
  phone?: string;
  address?: string;
  address2?: string;
  city?: string;
  region?: string;
  zip?: string;
  country?: string;
}

// Contact search state interface - lifted from component to parent
export interface ContactSearchState {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  contacts: Contact[];
  setContacts: Dispatch<SetStateAction<Contact[]>>;
  hasSearched: boolean;
  setHasSearched: Dispatch<SetStateAction<boolean>>;
  selectedEmailIndex: number;
  setSelectedEmailIndex: Dispatch<SetStateAction<number>>;
  selectedPhoneIndex: number;
  setSelectedPhoneIndex: Dispatch<SetStateAction<number>>;
  selectedAddressIndex: number;
  setSelectedAddressIndex: Dispatch<SetStateAction<number>>;
  isAddingNewEmail: boolean;
  setIsAddingNewEmail: Dispatch<SetStateAction<boolean>>;
  isAddingNewPhone: boolean;
  setIsAddingNewPhone: Dispatch<SetStateAction<boolean>>;
  isAddingNewAddress: boolean;
  setIsAddingNewAddress: Dispatch<SetStateAction<boolean>>;
}

// Props interface for ContactInformation component - uses generic types for flexibility
export interface ContactInformationProps<
  TFormData extends BaseContactFormData = BaseContactFormData,
  TTransaction extends BaseTransaction = BaseTransaction,
> {
  // Form data
  formData: TFormData;
  setFormData: Dispatch<SetStateAction<TFormData>>;

  // Merchant info
  merchant: Merchant | null;

  // Validation
  invalidFields: Set<string>;
  setInvalidFields: Dispatch<SetStateAction<Set<string>>>;
  handleFieldFocus: (fieldName: string) => void;

  // Drawer state
  isDrawerOpen: boolean;

  // Edit mode
  isEditMode: boolean;
  transaction?: TTransaction;

  // Contact state (passed from parent drawer)
  selectedContact: Contact | null;
  setSelectedContact: Dispatch<SetStateAction<Contact | null>>;
  isNewContact: boolean;
  setIsNewContact: Dispatch<SetStateAction<boolean>>;

  // Contact search state (lifted from component to parent for state persistence)
  contactSearchState?: ContactSearchState;
}

export default function ContactInformation<
  TFormData extends BaseContactFormData,
  TTransaction extends BaseTransaction,
>({
  formData,
  setFormData,
  merchant,
  invalidFields,
  setInvalidFields,
  handleFieldFocus,
  isDrawerOpen,
  isEditMode,
  transaction,
  selectedContact,
  setSelectedContact,
  isNewContact,
  setIsNewContact,
  contactSearchState,
}: ContactInformationProps<TFormData, TTransaction>) {
  // Track the previous transaction ID to detect actual transaction changes vs component remounts
  const prevTransactionIdRef = useRef<string | undefined>(undefined);

  // Add country settings
  const countrySettings = {
    cityText: 'City',
    regionText: 'State',
    postalCodeText: 'ZIP/Postal',
  };

  // Use lifted state from parent if provided, otherwise use local state (backward compatibility)
  const [localContacts, setLocalContacts] = useState<Contact[]>([]);
  const [localSearchTerm, setLocalSearchTerm] = useState<string>('');
  const [localHasSearched, setLocalHasSearched] = useState<boolean>(false);
  const [localSelectedEmailIndex, setLocalSelectedEmailIndex] = useState<number>(0);
  const [localSelectedPhoneIndex, setLocalSelectedPhoneIndex] = useState<number>(0);
  const [localSelectedAddressIndex, setLocalSelectedAddressIndex] = useState<number>(0);
  const [localIsAddingNewEmail, setLocalIsAddingNewEmail] = useState<boolean>(false);
  const [localIsAddingNewPhone, setLocalIsAddingNewPhone] = useState<boolean>(false);
  const [localIsAddingNewAddress, setLocalIsAddingNewAddress] = useState<boolean>(false);

  // Determine which state to use - lifted or local
  const contacts = contactSearchState?.contacts ?? localContacts;
  const setContacts = contactSearchState?.setContacts ?? setLocalContacts;
  const searchTerm = contactSearchState?.searchTerm ?? localSearchTerm;
  const setSearchTerm = contactSearchState?.setSearchTerm ?? setLocalSearchTerm;
  const hasSearched = contactSearchState?.hasSearched ?? localHasSearched;
  const setHasSearched = contactSearchState?.setHasSearched ?? setLocalHasSearched;
  const selectedEmailIndex = contactSearchState?.selectedEmailIndex ?? localSelectedEmailIndex;
  const setSelectedEmailIndex =
    contactSearchState?.setSelectedEmailIndex ?? setLocalSelectedEmailIndex;
  const selectedPhoneIndex = contactSearchState?.selectedPhoneIndex ?? localSelectedPhoneIndex;
  const setSelectedPhoneIndex =
    contactSearchState?.setSelectedPhoneIndex ?? setLocalSelectedPhoneIndex;
  const selectedAddressIndex =
    contactSearchState?.selectedAddressIndex ?? localSelectedAddressIndex;
  const setSelectedAddressIndex =
    contactSearchState?.setSelectedAddressIndex ?? setLocalSelectedAddressIndex;
  const isAddingNewEmail = contactSearchState?.isAddingNewEmail ?? localIsAddingNewEmail;
  const setIsAddingNewEmail = contactSearchState?.setIsAddingNewEmail ?? setLocalIsAddingNewEmail;
  const isAddingNewPhone = contactSearchState?.isAddingNewPhone ?? localIsAddingNewPhone;
  const setIsAddingNewPhone = contactSearchState?.setIsAddingNewPhone ?? setLocalIsAddingNewPhone;
  const isAddingNewAddress = contactSearchState?.isAddingNewAddress ?? localIsAddingNewAddress;
  const setIsAddingNewAddress =
    contactSearchState?.setIsAddingNewAddress ?? setLocalIsAddingNewAddress;

  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(false);

  // Add state for email and phone selection dropdowns (UI only, not persisted)
  const [showEmailDropdown, setShowEmailDropdown] = useState<boolean>(false);
  const [showPhoneDropdown, setShowPhoneDropdown] = useState<boolean>(false);

  // Add state for address selection dropdown (UI only, not persisted)
  const [showAddressDropdown, setShowAddressDropdown] = useState<boolean>(false);

  // Debounced search term - local only as it's derived from searchTerm
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Note: State reset when drawer opens is now handled by parent component
  // This prevents state from being reset when navigating between form steps
  // Only reset debouncedSearchTerm when searchTerm changes (handled by debounce effect below)

  // Search contacts effect
  useEffect(() => {
    const fetchContacts = async () => {
      if (!merchant?.merchantName || !debouncedSearchTerm || debouncedSearchTerm.length < 2) {
        setContacts([]);
        setHasSearched(false);
        return;
      }

      setIsLoadingContacts(true);
      setHasSearched(false);
      try {
        const token = await getAuthToken();
        const contactsData = await queryContactsTopContacts(
          token,
          [merchant.merchantName],
          '',
          '',
          '',
          debouncedSearchTerm,
        );

        if (contactsData && contactsData.length > 0) {
          const transformedContacts = contactsData.map(
            (contact: {
              contactID: string;
              name: string;
              email?: string;
              phone?: string;
              address?: string;
            }) => {
              const addressParts = contact.address ? contact.address.split(', ') : [];
              const address1 = addressParts[0] || '';
              const city = addressParts[2] || '';
              const region = addressParts[3] || '';
              const zip = addressParts[4] || '';
              const country = addressParts[5] || '';

              return {
                id: contact.contactID,
                fullName: contact.name,
                name: contact.name,
                organizationName: '',
                emails:
                  contact.email && contact.email !== '-'
                    ? [{ value: contact.email, is_primary: true }]
                    : [],
                phones: contact.phone ? [{ value: contact.phone, is_primary: true }] : [],
                addresses: [
                  {
                    line1: address1,
                    line2: '',
                    city: city,
                    region: region,
                    postal: zip,
                    country: country,
                    is_primary: true,
                  },
                ],
              };
            },
          );

          setContacts(transformedContacts);
        } else {
          setContacts([]);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
        setContacts([]);
      } finally {
        setIsLoadingContacts(false);
        setHasSearched(true);
      }
    };

    fetchContacts();
  }, [merchant?.merchantName, debouncedSearchTerm]);

  // Fetch contact data only when editing an existing transaction
  useEffect(() => {
    const fetchContact = async () => {
      if (!merchant?.merchantName || !isEditMode || !transaction?.contactID) {
        return;
      }

      // Skip fetching if we already have this contact loaded (prevents re-fetch on step navigation)
      if (selectedContact?.id === transaction.contactID) {
        return;
      }

      setIsLoadingContacts(true);
      try {
        const contactResponse = await fetch(
          `${searchReportingLegacyUrl}/validate/contact?contactID=${transaction.contactID}`,
          {
            method: 'GET',
            headers: {
              accept: '*/*',
              'content-type': 'application/json',
              Authorization: await getAuthToken(),
            },
          },
        );

        if (!contactResponse.ok) {
          console.error('Contact network response was not ok');
          return;
        }

        const responseData = await contactResponse.json();
        const contactData = responseData.contactInformation;
        if (contactData) {
          const filteredContactData = {
            ...contactData,
            emails: contactData.emails?.filter((email: ContactEmail) => !email?.deleted) || [],
            addresses:
              contactData.addresses?.filter((address: ContactAddress) => !address?.deleted) || [],
            phones: contactData.phones?.filter((phone: ContactPhone) => !phone?.deleted) || [],
          };

          if (isEditMode && transaction) {
            setSelectedContact(filteredContactData || null);

            // Find matching email index
            let emailIdx = 0;
            if (
              transaction?.email &&
              transaction.email !== '-' &&
              filteredContactData.emails?.length > 0
            ) {
              const matchingEmailIndex = filteredContactData.emails.findIndex(
                (email: ContactEmail) => email.email === transaction.email,
              );
              emailIdx =
                matchingEmailIndex !== -1
                  ? matchingEmailIndex
                  : (filteredContactData.emails?.findIndex(
                      (email: ContactEmail) => email.is_primary,
                    ) ?? 0);
            } else {
              emailIdx =
                filteredContactData.emails?.findIndex((email: ContactEmail) => email.is_primary) ??
                0;
            }
            const selectedEmail = filteredContactData.emails?.[emailIdx]?.email || '';

            // Find matching phone index
            let phoneIdx = 0;
            if (transaction?.phone && filteredContactData.phones?.length > 0) {
              const matchingPhoneIndex = filteredContactData.phones.findIndex(
                (phone: ContactPhone) => phone.value === transaction.phone,
              );
              phoneIdx =
                matchingPhoneIndex !== -1
                  ? matchingPhoneIndex
                  : (filteredContactData.phones?.findIndex(
                      (phone: ContactPhone) => phone.is_primary,
                    ) ?? 0);
            } else {
              phoneIdx =
                filteredContactData.phones?.findIndex((phone: ContactPhone) => phone.is_primary) ??
                0;
            }
            const selectedPhone = filteredContactData.phones?.[phoneIdx]?.value || '';

            // Find matching address index
            let addressIdx = 0;
            if (
              (transaction?.address ||
                transaction?.address2 ||
                transaction?.city ||
                transaction?.region ||
                transaction?.zip ||
                transaction?.country) &&
              filteredContactData.addresses?.length > 0
            ) {
              const transactionAddress = [
                transaction.address,
                transaction.address2,
                transaction.city,
                transaction.region,
                transaction.zip,
                transaction.country,
              ]
                .filter(Boolean)
                .join(', ')
                .toLowerCase()
                .trim();

              const matchingAddressIndex = filteredContactData.addresses.findIndex(
                (addr: ContactAddress) => {
                  const contactAddress = [
                    addr.line1,
                    addr.line2,
                    addr.city,
                    addr.region,
                    addr.postal,
                    addr.country,
                  ]
                    .filter(Boolean)
                    .join(', ')
                    .toLowerCase()
                    .trim();

                  return contactAddress === transactionAddress;
                },
              );

              addressIdx =
                matchingAddressIndex !== -1
                  ? matchingAddressIndex
                  : (filteredContactData.addresses?.findIndex(
                      (addr: ContactAddress) => addr.is_primary,
                    ) ?? 0);
            } else {
              addressIdx =
                filteredContactData.addresses?.findIndex(
                  (addr: ContactAddress) => addr.is_primary,
                ) ?? 0;
            }
            const selectedAddress =
              filteredContactData.addresses?.[addressIdx] ||
              filteredContactData.addresses?.find((addr: ContactAddress) => addr.is_primary) ||
              filteredContactData.addresses?.[0] ||
              {};

            setSelectedEmailIndex(emailIdx);
            setSelectedPhoneIndex(phoneIdx);
            setSelectedAddressIndex(addressIdx);

            setFormData((prevData) => ({
              ...prevData,
              contactID: filteredContactData?.id || '',
              firstName: filteredContactData?.firstName || '',
              lastName: filteredContactData?.lastName || '',
              email: selectedEmail,
              phone: selectedPhone,
              address1: selectedAddress.line1,
              address2: selectedAddress.line2,
              city: selectedAddress.city,
              region: selectedAddress.region,
              zip: selectedAddress.postal,
              country: selectedAddress.country,
              organizationName: filteredContactData?.organizationName || '',
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
      } finally {
        setIsLoadingContacts(false);
      }
    };

    fetchContact();
  }, [merchant?.merchantName, isEditMode, transaction?.contactID, setFormData, setSelectedContact]);

  // Update the handleAddressChange function
  const handleAddressChange = (e: AddressAutocompleteEvent) => {
    const { features } = e;
    if (features && features.length > 0) {
      const address = features[0].properties;

      const countryObj = countries.find(
        (c: CountryOption) => c.value.toLowerCase() === address.country_code?.toLowerCase(),
      );

      const countryName = countryObj ? countryObj.label : '';
      setFormData((prevData) => ({
        ...prevData,
        address1: address.address_line1 || '',
        address2: address.address_line2 || '',
        city: address.city || address.place || '',
        region: address.state_code || address.state || address.region_code || '',
        zip: address.postcode || '',
        country: countryName || '',
      }));
    }
  };

  // Helper function to safely check if a field is invalid
  const isFieldInvalid = (fieldName: string): boolean => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      return invalidFields.has(fieldName);
    }
    return false;
  };

  // Phone validation function
  const validatePhone = (phone: string): boolean => {
    if (!phone?.trim()) return true;

    const cleanPhone = phone.replace(/[\s\-().]/g, '');
    const phoneToValidate = cleanPhone.startsWith('+') ? cleanPhone : '+' + cleanPhone;

    try {
      const phoneNumber = parsePhoneNumberFromString(phoneToValidate);

      if (!phoneNumber) return false;

      return phoneNumber.isValid() && isPossiblePhoneNumber(phoneToValidate);
    } catch {
      return false;
    }
  };

  // Email and phone selection handlers
  const handleEmailChange = (emailIndex: number) => {
    handleFieldFocus('email');
    if (emailIndex === -1) {
      setIsAddingNewEmail(true);
      setSelectedEmailIndex(-1);
      setFormData((prev) => ({ ...prev, email: '' }));
    } else {
      setIsAddingNewEmail(false);
      setSelectedEmailIndex(emailIndex);
      const selectedEmail = selectedContact?.emails?.[emailIndex];
      setFormData((prev) => ({ ...prev, email: selectedEmail?.email ?? '' }));
    }
    setShowEmailDropdown(false);
  };

  const handlePhoneChange = (phoneIndex: number) => {
    if (phoneIndex === -1) {
      setIsAddingNewPhone(true);
      setSelectedPhoneIndex(-1);
      setFormData((prev) => ({ ...prev, phone: '' }));
    } else {
      setIsAddingNewPhone(false);
      setSelectedPhoneIndex(phoneIndex);
      const selectedPhone = selectedContact?.phones?.[phoneIndex];
      setFormData((prev) => ({ ...prev, phone: selectedPhone?.value ?? '' }));
    }
    setShowPhoneDropdown(false);
  };

  const toggleEmailDropdown = () => {
    setShowEmailDropdown(!showEmailDropdown);
    setShowPhoneDropdown(false);
  };

  const togglePhoneDropdown = () => {
    setShowPhoneDropdown(!showPhoneDropdown);
    setShowEmailDropdown(false);
  };

  // Address selection handlers
  const handleAddressSelection = (addressIndex: number) => {
    if (addressIndex === -1) {
      setIsAddingNewAddress(true);
      setSelectedAddressIndex(-1);
      setFormData((prev) => ({
        ...prev,
        address1: '',
        address2: '',
        city: '',
        region: '',
        zip: '',
        country: '',
      }));
    } else {
      setIsAddingNewAddress(false);
      setSelectedAddressIndex(addressIndex);
      const selectedAddress = selectedContact?.addresses?.[addressIndex];
      setFormData((prev) => ({
        ...prev,
        address1: selectedAddress?.line1 || '',
        address2: selectedAddress?.line2 || '',
        city: selectedAddress?.city || '',
        region: selectedAddress?.region || '',
        zip: selectedAddress?.postal || '',
        country: selectedAddress?.country || '',
      }));
    }
    setShowAddressDropdown(false);
  };

  const toggleAddressDropdown = () => {
    setShowAddressDropdown(!showAddressDropdown);
    setShowEmailDropdown(false);
    setShowPhoneDropdown(false);
  };

  const updateRelatedContactDetails = (contact: Contact) => {
    const primaryEmailIndex =
      contact.emails?.findIndex((email: ContactEmail) => email.is_primary) ?? 0;
    const primaryEmail = contact.emails?.[primaryEmailIndex]?.email || '';

    const primaryPhoneIndex =
      contact.phones?.findIndex((phone: ContactPhone) => phone.is_primary) ?? 0;
    const primaryPhone = contact.phones?.[primaryPhoneIndex]?.value || '';

    const primaryAddressIndex =
      contact.addresses?.findIndex((addr: ContactAddress) => addr.is_primary) ?? 0;
    const primaryAddress =
      contact.addresses?.find((addr: ContactAddress) => addr.is_primary) ||
      contact.addresses?.[0] ||
      {};

    setSelectedEmailIndex(primaryEmailIndex);
    setSelectedPhoneIndex(primaryPhoneIndex);
    setSelectedAddressIndex(primaryAddressIndex);

    handleFieldFocus('contactSearch');
    handleFieldFocus('firstName');
    handleFieldFocus('lastName');
    handleFieldFocus('email');

    setFormData((prev) => ({
      ...prev,
      contactID: contact.id,
      contactSearch: '',
      email: primaryEmail,
      firstName: contact.firstName,
      lastName: contact.lastName,
      organizationName: contact.organizationName,
      phone: primaryPhone,
      address1: primaryAddress.line1,
      address2: primaryAddress.line2,
      city: primaryAddress.city,
      region: primaryAddress.region,
      zip: primaryAddress.postal,
      country: primaryAddress.country,
    }));
  };

  const handleContactSelect = async (contact: Contact) => {
    if (!contact?.id) {
      return;
    }

    setInvalidFields((prev) => {
      const newFields = new Set(prev);
      newFields.delete('contactSearch');
      newFields.delete('contact_information-contactSearch');
      return newFields;
    });

    setIsLoadingContacts(true);
    try {
      const contactResponse = await fetch(
        `${searchReportingLegacyUrl}/validate/contact?contactID=${contact.id}`,
        {
          method: 'GET',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
        },
      );

      if (!contactResponse.ok) {
        console.error('Contact network response was not ok');
        return;
      }

      const responseData = await contactResponse.json();
      const contactData = responseData.contactInformation;

      if (contactData) {
        const filteredContactData = {
          ...contactData,
          emails: contactData.emails?.filter((email: ContactEmail) => !email?.deleted) || [],
          addresses:
            contactData.addresses?.filter((address: ContactAddress) => !address?.deleted) || [],
          phones: contactData.phones?.filter((phone: ContactPhone) => !phone?.deleted) || [],
        };
        setSelectedContact(filteredContactData);
        updateRelatedContactDetails(filteredContactData);
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
      setSelectedContact(contact);
      updateRelatedContactDetails(contact);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // Add a reset function for contact-related states
  const resetContactStates = useCallback(() => {
    setSelectedContact(null);
    setIsNewContact(false);
    setShowEmailDropdown(false);
    setShowPhoneDropdown(false);
    setSelectedEmailIndex(0);
    setSelectedPhoneIndex(0);
    setIsAddingNewEmail(false);
    setIsAddingNewPhone(false);
    setShowAddressDropdown(false);
    setSelectedAddressIndex(0);
    setIsAddingNewAddress(false);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setHasSearched(false);
    setContacts([]);
    setIsLoadingContacts(false);
  }, [setSelectedContact, setIsNewContact]);

  // Reset contact states only when transaction ID actually CHANGES (not on initial mount)
  // This prevents the flicker when navigating between form steps in edit mode
  useEffect(() => {
    const currentTransactionId = transaction?.transactionID;
    const prevTransactionId = prevTransactionIdRef.current;

    // Only reset if we had a previous transaction and it's different from the current one
    // This allows the initial load to work without resetting, but will reset when switching transactions
    if (
      isEditMode &&
      currentTransactionId &&
      prevTransactionId &&
      prevTransactionId !== currentTransactionId
    ) {
      resetContactStates();
    }

    // Update the ref to track the current transaction ID
    prevTransactionIdRef.current = currentTransactionId;
  }, [transaction?.transactionID, isEditMode, resetContactStates]);

  return (
    <>
      <div>
        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
          Contact
        </Label>
        <ContactMultiSelectDropdown
          contacts={
            contacts as {
              id: string;
              fullName?: string;
              name?: string;
              organizationName?: string;
              emails?: { value?: string; is_primary?: boolean }[];
            }[]
          }
          isLoadingContacts={isLoadingContacts}
          searchValue={searchTerm}
          onSearchChange={(value) => {
            handleFieldFocus('contactSearch');
            setSearchTerm(value);
            setFormData((prev) => ({ ...prev, contactSearch: value }));
          }}
          onContactSelect={(contact: {
            id: string;
            fullName?: string;
            name?: string;
            organizationName?: string;
            emails?: { value?: string; is_primary?: boolean }[];
          }) => {
            handleContactSelect(contact as Contact);
          }}
          onCreateContact={(newContactData) => {
            setSelectedContact(null);
            setIsNewContact(true);
            setFormData((prev) => ({
              ...prev,
              contactSearch: '',
              ...newContactData,
            }));

            setInvalidFields((prev) => {
              const newFields = new Set(prev);
              newFields.delete('contactSearch');
              newFields.delete('contact_information-contactSearch');
              newFields.delete('firstName');
              newFields.delete('contact_information-firstName');
              newFields.delete('lastName');
              newFields.delete('contact_information-lastName');
              newFields.delete('email');
              newFields.delete('contact_information-email');
              return newFields;
            });

            setSearchTerm('');
            setShowEmailDropdown(false);
            setShowPhoneDropdown(false);
            setSelectedEmailIndex(0);
            setSelectedPhoneIndex(0);
            setIsAddingNewEmail(false);
            setIsAddingNewPhone(false);
          }}
          placeholder="Create/Search for contact"
          isInvalid={isFieldInvalid('contactSearch')}
          hasSearched={hasSearched}
        />
      </div>

      {/* Selected Contact Card */}
      {selectedContact && (
        <div className="mt-3 rounded-md border border-edge bg-paper-muted p-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="font-medium text-ink">
                {selectedContact.fullName || selectedContact.name || 'Unknown Name'}
              </div>
              {selectedContact.emails
                ?.filter((email) => !email.deleted)
                .find((email) => email.is_primary) && (
                <div className="text-sm text-ink-muted">
                  {
                    selectedContact.emails
                      ?.filter((email) => !email.deleted)
                      .find((email) => email.is_primary)?.email
                  }
                </div>
              )}
              {selectedContact.organizationName && (
                <div className="text-xs text-ink-muted">{selectedContact.organizationName}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedContact(null);
                setIsNewContact(false);
                setFormData((prev) => ({
                  ...prev,
                  contactSearch: '',
                  firstName: '',
                  lastName: '',
                  email: '',
                  organizationName: '',
                  phone: '',
                  address1: '',
                  address2: '',
                  city: '',
                  region: '',
                  zip: '',
                  country: '',
                  contactID: '',
                }));

                setShowEmailDropdown(false);
                setShowPhoneDropdown(false);
                setSelectedEmailIndex(0);
                setSelectedPhoneIndex(0);
              }}
              className="text-ink-muted hover:text-ink-secondary"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      {(selectedContact || isNewContact) && (
        <div className="mt-2 space-y-3">
          {/* First Name and Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                First Name
              </Label>
              <Input
                type="text"
                value={formData.firstName || ''}
                disabled={!!(selectedContact && selectedContact.firstName)}
                onChange={(e) => {
                  handleFieldFocus('firstName');
                  setFormData((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }));
                }}
                className={`h-7 border bg-white ${
                  isFieldInvalid('firstName') ? 'border-red-500' : 'border-edge'
                }`}
                autoComplete="given-name"
                onFocus={() => handleFieldFocus('firstName')}
              />
            </div>
            <div>
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Last Name
              </Label>
              <Input
                type="text"
                value={formData.lastName || ''}
                disabled={!!(selectedContact && selectedContact.lastName)}
                onChange={(e) => {
                  handleFieldFocus('lastName');
                  setFormData((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }));
                }}
                className={`h-7 border bg-white ${
                  isFieldInvalid('lastName') ? 'border-red-500' : 'border-edge'
                }`}
                autoComplete="family-name"
                onFocus={() => handleFieldFocus('lastName')}
              />
            </div>
          </div>

          {/* Org Name */}
          <div>
            <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
              Organization Name
            </Label>
            <Input
              type="text"
              value={formData.organizationName || ''}
              onChange={(e) => {
                handleFieldFocus('organizationName');
                setFormData((prev) => ({
                  ...prev,
                  organizationName: e.target.value,
                }));
              }}
              className={`h-7 border bg-white ${
                isFieldInvalid('organizationName') ? 'border-red-500' : 'border-edge'
              }`}
              autoComplete="organization"
              onFocus={() => handleFieldFocus('organizationName')}
            />
          </div>

          {/* email & phone */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Email Field */}
            <div className="relative">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Email (optional)
              </Label>
              <div className="relative">
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => {
                    handleFieldFocus('email');
                    setFormData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }));
                  }}
                  disabled={
                    !!(
                      selectedContact &&
                      selectedContact.emails &&
                      selectedEmailIndex >= 0 &&
                      !isAddingNewEmail
                    )
                  }
                  className={`h-7 border bg-white ${
                    isFieldInvalid('email') ? 'border-red-500' : 'border-edge'
                  } ${
                    selectedContact &&
                    selectedContact.emails &&
                    selectedEmailIndex >= 0 &&
                    !isAddingNewEmail
                      ? 'input-text-ellipsis cursor-not-allowed bg-paper-inset pr-24 text-ink-muted'
                      : selectedContact && selectedContact.emails && selectedEmailIndex === -1
                        ? 'pr-28'
                        : selectedContact &&
                            selectedContact.emails &&
                            selectedContact.emails.length > 0
                          ? 'pr-24'
                          : 'pr-2'
                  }`}
                  autoComplete="email"
                  placeholder="Enter email address"
                  onFocus={() => handleFieldFocus('email')}
                />

                {/* Email Badge - Inside input on the right */}
                {selectedContact &&
                  selectedContact.emails &&
                  selectedEmailIndex >= 0 &&
                  selectedContact.emails[selectedEmailIndex] && (
                    <div className="absolute right-7 top-[13px] -translate-y-1/2">
                      {selectedContact.emails[selectedEmailIndex].is_primary ? (
                        <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                          Primary
                        </span>
                      ) : (
                        <span className="rounded bg-paper-inset px-1.5 py-0.5 text-xs text-ink-secondary">
                          Secondary
                        </span>
                      )}
                    </div>
                  )}
                {selectedContact && selectedContact.emails && selectedEmailIndex === -1 && (
                  <div className="absolute right-7 top-[13px] -translate-y-1/2">
                    <span className="rounded bg-accent/10 px-1.5 py-0.5 text-xs text-accent">
                      New Email
                    </span>
                  </div>
                )}

                {selectedContact && selectedContact.emails && selectedContact.emails.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleEmailDropdown}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Email Dropdown */}
              {showEmailDropdown &&
                selectedContact &&
                selectedContact.emails &&
                selectedContact.emails.length > 0 && (
                  <div className="absolute top-12 z-50 mt-1 w-full rounded-md border border-edge bg-white shadow-lg">
                    <div className="max-h-60 overflow-auto">
                      {selectedContact.emails.map((email, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleEmailChange(index)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-paper-inset focus:bg-paper-inset focus:outline-none ${
                            index === selectedEmailIndex ? 'bg-paper-inset' : ''
                          }`}
                        >
                          <div className="min-w-0 flex-1 font-medium text-ink">
                            <div className="input-text-ellipsis">{email.value}</div>
                          </div>
                          {email.is_primary ? (
                            <span className="flex-shrink-0 rounded bg-green-100 px-0.5 py-0.5 pr-2 text-xs text-green-800">
                              Primary
                            </span>
                          ) : (
                            <span className="flex-shrink-0 rounded bg-paper-inset px-0.5 py-0.5 pr-2 text-xs text-ink-secondary">
                              Secondary
                            </span>
                          )}
                        </button>
                      ))}
                      <div className="border-t border-edge">
                        <button
                          type="button"
                          onClick={() => handleEmailChange(-1)}
                          className="w-full px-3 py-1 text-left text-sm text-green-600 hover:bg-paper-inset focus:bg-paper-inset focus:outline-none"
                        >
                          <div className="text-sm">Add New Email</div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>

            <div className="relative">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Phone (optional)
              </Label>
              <div className="relative">
                <PhoneInput
                  country={'us'}
                  value={formData.phone || ''}
                  onChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      phone: value,
                    }));

                    if (value && value.trim()) {
                      const isValid = validatePhone(value);
                      setInvalidFields((prev) => {
                        const newInvalidFields = new Set(prev);
                        if (!isValid) {
                          newInvalidFields.add('phone');
                        } else {
                          newInvalidFields.delete('phone');
                        }
                        return newInvalidFields;
                      });
                    } else {
                      setInvalidFields((prev) => {
                        const newInvalidFields = new Set(prev);
                        newInvalidFields.delete('phone');
                        return newInvalidFields;
                      });
                    }
                  }}
                  disableSearchIcon={true}
                  buttonStyle={{
                    borderRadius: '6px 0px 0px 6px',
                    border: isFieldInvalid(`phone`) ? '1px solid #ef4444' : '1px solid #E0D9CC',
                    ...(selectedContact &&
                      selectedContact.phones &&
                      selectedPhoneIndex >= 0 &&
                      !isAddingNewPhone && {
                        color: '#94a3b8',
                        backgroundColor: '#F5F3ED',
                        cursor: 'not-allowed',
                      }),
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '28px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '6px',
                    border: isFieldInvalid(`phone`) ? '1px solid #ef4444' : '1px solid #E0D9CC',
                    ...(selectedContact &&
                      selectedContact.phones &&
                      selectedPhoneIndex >= 0 &&
                      !isAddingNewPhone && {
                        color: '#94a3b8',
                        backgroundColor: '#FFFFFF',
                        cursor: 'not-allowed',
                        paddingRight: '6rem',
                      }),
                    ...(selectedContact &&
                      selectedContact.phones &&
                      selectedPhoneIndex === -1 && {
                        paddingRight: '7rem',
                      }),
                    ...(selectedContact &&
                      selectedContact.phones &&
                      selectedContact.phones.length > 0 &&
                      selectedPhoneIndex < 0 && {
                        paddingRight: '6rem',
                      }),
                    ...((!selectedContact ||
                      !selectedContact.phones ||
                      selectedContact.phones.length === 0) && {
                      paddingRight: '0.5rem',
                    }),
                  }}
                  preferredCountries={['us', 'ca']}
                  enableSearch={true}
                  dropdownStyle={{
                    width: 'full',
                    borderRadius: '6px',
                  }}
                  searchStyle={{
                    width: '92%',
                    borderRadius: '6px',
                  }}
                  inputProps={{
                    name: `phone`,
                  }}
                  onFocus={() => {
                    handleFieldFocus('phone');
                    setInvalidFields((prev) => {
                      const newInvalidFields = new Set(prev);
                      newInvalidFields.delete('phone');
                      return newInvalidFields;
                    });
                  }}
                  placeholder="Enter phone number"
                  disabled={
                    !!(
                      selectedContact &&
                      selectedContact.phones &&
                      selectedPhoneIndex >= 0 &&
                      !isAddingNewPhone
                    )
                  }
                />

                {/* Phone Badge - Inside input on the right */}
                {selectedContact &&
                  selectedContact.phones &&
                  selectedPhoneIndex >= 0 &&
                  selectedContact.phones[selectedPhoneIndex] && (
                    <div className="absolute right-7 top-[13px] -translate-y-1/2">
                      {selectedContact.phones[selectedPhoneIndex].is_primary ? (
                        <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                          Primary
                        </span>
                      ) : (
                        <span className="rounded bg-paper-inset px-1.5 py-0.5 text-xs text-ink-secondary">
                          Secondary
                        </span>
                      )}
                    </div>
                  )}
                {selectedContact && selectedContact.phones && selectedPhoneIndex === -1 && (
                  <div className="absolute right-7 top-[13px] -translate-y-1/2">
                    <span className="rounded bg-accent/10 px-1.5 py-0.5 text-xs text-accent">
                      New Phone
                    </span>
                  </div>
                )}

                {selectedContact && selectedContact.phones && selectedContact.phones.length > 0 && (
                  <button
                    type="button"
                    onClick={togglePhoneDropdown}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Phone Dropdown */}
              {showPhoneDropdown &&
                selectedContact &&
                selectedContact.phones &&
                selectedContact.phones.length > 0 && (
                  <div className="absolute top-12 z-50 mt-1 w-full rounded-md border border-edge bg-white shadow-lg">
                    <div className="max-h-60 overflow-auto">
                      {selectedContact.phones.map((phone, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handlePhoneChange(index)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-paper-inset focus:bg-paper-inset focus:outline-none ${
                            index === selectedPhoneIndex ? 'bg-paper-inset' : ''
                          }`}
                        >
                          <div className="min-w-0 flex-1 font-medium text-ink">
                            <div className="input-text-ellipsis">{phone.value}</div>
                          </div>
                          {phone.is_primary ? (
                            <span className="flex-shrink-0 rounded bg-green-100 px-0.5 py-0.5 pr-2 text-xs text-green-800">
                              Primary
                            </span>
                          ) : (
                            <span className="flex-shrink-0 rounded bg-paper-inset px-0.5 py-0.5 pr-2 text-xs text-ink-secondary">
                              Secondary
                            </span>
                          )}
                        </button>
                      ))}
                      <div className="border-t border-edge">
                        <button
                          type="button"
                          onClick={() => handlePhoneChange(-1)}
                          className="w-full px-3 py-1 text-left text-sm text-green-500 hover:bg-paper-inset focus:bg-paper-inset focus:outline-none"
                        >
                          <div className="text-sm">Add New Phone</div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
          <div>
            <div className="pl-1">
              <Checkbox
                checked={formData.anonymous || false}
                onChange={(anonymous) =>
                  setFormData((prev) => ({
                    ...prev,
                    anonymous,
                  }))
                }
                label="Don't display the contact's name anywhere publicly"
              />
            </div>
          </div>

          {/* Communication Consent Checkbox */}
          <div>
            <div className="pl-1">
              <Checkbox
                checked={formData.communicationConsent || false}
                onChange={(communicationConsent) =>
                  setFormData((prev) => ({
                    ...prev,
                    communicationConsent,
                  }))
                }
                label={`Allow ${merchant?.merchantName || 'this organization'} to contact me`}
              />
            </div>
          </div>

          {/* Address Section */}
          <div className="space-y-3">
            {/* Address Selection Badge */}
            {selectedContact &&
              selectedContact.addresses &&
              selectedContact.addresses.length > 0 &&
              !isAddingNewAddress && (
                <div className="relative pb-2">
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Address
                  </Label>
                  <div className="relative">
                    <button
                      type="button"
                      data-field-surface
                      onClick={toggleAddressDropdown}
                      className="flex h-full w-full items-center justify-between rounded-md border border-edge bg-paper-muted px-3 py-2 text-left text-sm hover:bg-paper-inset focus:outline-none focus:ring-2 focus:ring-sage"
                    >
                      <div className="flex h-full w-full items-center justify-between gap-2">
                        <div className="flex-1">
                          <div className="font-medium text-ink">
                            {formData.address1}
                            {formData.address2 && `, ${formData.address2}`}
                          </div>
                          <div className="text-ink-muted">
                            {formData.city && `${formData.city}`}
                            {formData.region && `, ${formData.region}`}
                            {formData.zip && `, ${formData.zip}`}
                            {formData.country && `, ${formData.country}`}
                          </div>
                        </div>
                        {/* Address Badge */}
                        {selectedContact.addresses[selectedAddressIndex] && (
                          <div className="h-full self-start">
                            {selectedContact.addresses[selectedAddressIndex].is_primary ? (
                              <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                                Primary
                              </span>
                            ) : (
                              <span className="rounded bg-paper-inset px-1.5 py-0.5 text-xs text-ink-secondary">
                                Secondary
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <svg
                        className="h-4 w-4 text-ink-muted"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Address Dropdown */}
                  {showAddressDropdown && selectedContact.addresses.length > 0 && (
                    <div className="absolute bottom-16 z-50 mb-1 w-full rounded-md border border-edge bg-white shadow-lg">
                      <div className="max-h-60 overflow-auto">
                        <div className="border-b border-edge">
                          <button
                            type="button"
                            onClick={() => handleAddressSelection(-1)}
                            className="w-full px-3 py-1 text-left text-sm text-green-600 hover:bg-paper-inset focus:bg-paper-inset focus:outline-none"
                          >
                            <div className="text-sm">Add New Address</div>
                          </button>
                        </div>
                        {selectedContact.addresses.map((address, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleAddressSelection(index)}
                            className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-paper-inset focus:bg-paper-inset focus:outline-none ${
                              index === selectedAddressIndex ? 'bg-paper-inset' : ''
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="input-text-ellipsis pr-2 font-medium text-ink">
                                {address.line1}
                                {address.line2 && `, ${address.line2}`}
                              </div>
                              <div className="input-text-ellipsis pr-2 text-ink-muted">
                                {address.city && `${address.city}`}
                                {address.region && `, ${address.region}`}
                                {address.postal && `, ${address.postal}`}
                              </div>
                              <div className="input-text-ellipsis pr-2 text-xs text-ink-muted">
                                {address.country}
                              </div>
                            </div>
                            {address.is_primary ? (
                              <span className="flex-shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                                Primary
                              </span>
                            ) : (
                              <span className="flex-shrink-0 rounded bg-paper-inset px-1.5 py-0.5 text-xs text-ink-secondary">
                                Secondary
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Address Input Fields - Show when adding new address or no addresses exist */}
            {(isAddingNewAddress || !selectedContact?.addresses?.length) && (
              <AddressAutofill
                accessToken={import.meta.env.VITE_APP_SCOPED_MAPBOX_TOKEN}
                onRetrieve={handleAddressChange}
              >
                <div className="space-y-3">
                  {/* Address Line 1 */}
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Address 1
                    </Label>
                    <Input
                      type="text"
                      value={formData.address1 || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          address1: e.target.value,
                        }))
                      }
                      className={`h-7 border bg-white ${
                        isFieldInvalid('address1') ? 'border-red-500' : 'border-edge'
                      }`}
                      autoComplete="address-line1"
                      onFocus={() => handleFieldFocus('address1')}
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Address 2
                    </Label>
                    <Input
                      type="text"
                      value={formData.address2 || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          address2: e.target.value,
                        }))
                      }
                      className={`h-7 border bg-white ${
                        isFieldInvalid('address2') ? 'border-red-500' : 'border-edge'
                      }`}
                      autoComplete="address-line2"
                      onFocus={() => handleFieldFocus('address2')}
                    />
                  </div>

                  {/* City, State, ZIP */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        {countrySettings.cityText}
                      </Label>
                      <Input
                        type="text"
                        value={formData.city || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            city: e.target.value,
                          }))
                        }
                        className={`h-7 border bg-white ${
                          isFieldInvalid('city') ? 'border-red-500' : 'border-edge'
                        }`}
                        autoComplete="address-level2"
                        onFocus={() => handleFieldFocus('city')}
                      />
                    </div>
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        {countrySettings.regionText}
                      </Label>
                      <Input
                        type="text"
                        value={formData.region || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            region: e.target.value,
                          }))
                        }
                        className={`h-7 border bg-white ${
                          isFieldInvalid('state') ? 'border-red-500' : 'border-edge'
                        }`}
                        maxLength={2}
                        autoComplete="address-level1"
                        onFocus={() => handleFieldFocus('state')}
                      />
                    </div>
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        {countrySettings.postalCodeText}
                      </Label>
                      <Input
                        type="text"
                        value={formData.zip || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            zip: e.target.value,
                          }))
                        }
                        className={`h-7 border bg-white ${
                          isFieldInvalid('zip') ? 'border-red-500' : 'border-edge'
                        }`}
                        maxLength={6}
                        autoComplete="postal-code"
                        onFocus={() => handleFieldFocus('zip')}
                      />
                    </div>
                  </div>
                  {/* Country */}
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Country
                    </Label>
                    <select
                      value={formData.country || ''}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          country: e.target.value,
                        }));
                      }}
                      className="block h-7 w-full rounded-md border-edge bg-white px-2 py-0 text-sm leading-tight focus:border-sage focus:outline-none focus:ring-sage"
                      autoComplete="country"
                      onFocus={() => handleFieldFocus('country')}
                    >
                      <option value="">Select a country</option>
                      {formData.country && !countryLabels.has(formData.country) && (
                        <option value={formData.country}>{formData.country}</option>
                      )}
                      {countries.map((country) => (
                        <option key={country.value} value={country.label}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </AddressAutofill>
            )}

            {/* Back to Address Selection Button - Only show when adding new address */}
            {isAddingNewAddress && (selectedContact?.addresses?.length ?? 0) > 0 && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => {
                    const primaryAddressIndex =
                      selectedContact?.addresses?.findIndex((addr) => addr.is_primary) ?? -1;
                    const addressIndexToUse = primaryAddressIndex >= 0 ? primaryAddressIndex : 0;

                    setIsAddingNewAddress(false);
                    setSelectedAddressIndex(addressIndexToUse);

                    const selectedAddress = selectedContact?.addresses?.[addressIndexToUse];
                    if (selectedAddress) {
                      setFormData((prev) => ({
                        ...prev,
                        address1: selectedAddress.line1 || '',
                        address2: selectedAddress.line2 || '',
                        city: selectedAddress.city || '',
                        region: selectedAddress.region || '',
                        zip: selectedAddress.postal || '',
                        country: selectedAddress.country || '',
                      }));
                    }
                  }}
                  className="text-sm text-ink-secondary underline hover:text-ink"
                >
                  ← Back to existing addresses
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
